import {AfterViewInit, Component, Injector, OnDestroy} from '@angular/core';
import {FrontendFormComponent} from '@abstract/frontend.form.component';
import {ActivatedRoute, Router} from '@angular/router';
import {EquipmentCardInterfaces, EquipmentEnums, EquipmentInterfaces} from '@namespaces/equipment.namespace';
import {Anchor} from '@layoutComponents/page-inside-navigation/anchor.interface';
import {Subscription} from 'rxjs';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import {ScrollToService} from '@services/scroll-to.service';
import {EquipmentSearchService} from '../services/equipment-search.service';
import {EquipmentCreateOptionsService} from '@services/equipment-create-options.service';
import {AuthService} from '@services/auth.service';
import {UserNamespace} from '@namespaces/user.namespace';
import {RoutesStorage} from '@helpers/routes.helper';
import {NotyMessagesEnum} from '@helpers/noty-messages.enum';
import {CardApiService} from '@modules/equipment-card/services/card.api.service';

@Component({
    providers: [
        {provide: 'EquipmentSearchService', useClass: EquipmentSearchService},
        EquipmentCreateOptionsService,
    ],
    selector: 'apparatus-card-form',
    templateUrl: './apparatus-card.component.html',
})
export class ApparatusCardComponent extends FrontendFormComponent implements OnDestroy, AfterViewInit {
    /**
     * Выбранная модель оборудования
     * @type {any}
     */
    public selectedApparatus: EquipmentInterfaces.Model = null;

    public types = EquipmentEnums.Type;

    public anchors: Anchor[] = [];

    public canEditCalibrations = false;

    public afterSaveMessage = NotyMessagesEnum.equipmentCardUpdatedMessage;

    public backButtonUrl: string;
    public backButtonLabel: string;

    private subs: Subscription[] = [];

    constructor(
        injector: Injector,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private authService: AuthService,
        public formService: CardApiService,
    ) {
        super(injector);
    }

    init() {
        this.form = this._fb.group({
            id: [''],
            apparatus_id: [''],
            responsible_employee_id: [''],
            calibrations: this._fb.array([]),
            equipments: this._fb.array([]),
            repairs: this._fb.array([]),
            places: this._fb.array([])
        });

        const formValues: EquipmentCardInterfaces.Card.Item = this.activatedRoute.snapshot.data.formValues;

        if (formValues) {
            this.setFormValues(formValues, this.form);

            /**
             * Восстанавливаем выбранную модель сразу при редактировании
             */
            if (formValues.apparatus) {
                this.selectedApparatus = formValues.apparatus;
            }

            this.backButtonUrl = RoutesStorage.Equipment.view(formValues.id);
            this.backButtonLabel = 'Карточка оборудования';
        } else {
            this.backButtonUrl = RoutesStorage.Equipment.list;
            this.backButtonLabel = 'Вернуться в склад';
        }

        this.createAnchors();

        this.subs.push(
            this.form.get('calibrations').statusChanges.pipe(
                debounceTime(30),
                distinctUntilChanged(),
            ).subscribe(() => {
                this.setAnchorErrorStatus('calibrations', this.form.get('calibrations').valid);
            })
        );

        this.canEditCalibrations = this.authService.hasPermissions([
            UserNamespace.Permission.equipmentCardCalibrationAdd,
            UserNamespace.Permission.equipmentCardCalibrationUpdate,
        ]);
    }

    ngAfterViewInit() {
        const fragment = this.activatedRoute.snapshot.fragment;

        if (!fragment) {
            return;
        }

        /**
         * Без таймаута что-то странное происходит с View и скролл на долю секунды ломает верстку, так что такой костылик пока необходим
         * Возможно это из-за подгружаемого блока с выбором модели оборудования
         */
        setTimeout(() => {
            ScrollToService.toById(fragment);
        }, 100);
    }

    /**
     * Обработчик выбора оборудования из справочника
     * @param {EquipmentInterfaces.Model} item
     */
    onSelect(item: EquipmentInterfaces.Model) {
        this.selectedApparatus = item;

        if (!item) {
            this.reset();
        }
        this.createAnchors();
    }

    /**
     * Предотвращаем отправку формы, пока не выбрана модель.
     * Нужно в основном для предоствращения отправки через ctrl + Enter
     * @returns {boolean}
     */
    preventSave() {
        return !this.selectedApparatus;
    }


    afterSave() {
        super.afterSave();
        /**
         * Для нового url идет как /new, для редактирования как /edit/:id, поэтому и пути до view будут разные
         * @type {string}
         */
        const {id} = this.form.value;
        this.router.navigate([RoutesStorage.Equipment.view(id)]);
    }

    setAnchorErrorStatus(targetId: string, valid: boolean) {
        if (!targetId) {
            return;
        }

        const anchor = this.anchors.find(a => a.targetId === targetId);

        if (!anchor) {
            return;
        }

        anchor.error = !valid;

        this.anchors = [...this.anchors];
    }

    ngOnDestroy() {
        this.subs.forEach(s => s.unsubscribe());
    }

    private createAnchors() {
        this.anchors = [
            {title: 'Модель', targetId: 'model', error: false, active: true},
        ];

        if (!this.selectedApparatus) {
            return;
        }

        this.anchors.push({title: 'Данные оборудования', targetId: 'common', error: false});

        if (this.selectedApparatus.type !== this.types.material && this.canEditCalibrations) {
            this.anchors.push({title: 'Поверка', targetId: 'calibrations', error: false});
            this.anchors.push({title: 'Ремонт', targetId: 'repairs', error: false});
        }
    }
}
